import { useNavigate } from 'react-router-dom';
import { Folder as FolderIcon } from 'lucide-react';
import { formatRelativeTime } from '@common/utils/format';
import { ResourceActionMenu } from '@common/components/ResourceActionMenu';
import { SharedBadge } from '@common/components/SharedBadge';
import type { Folder } from '../types';

interface FolderCardProps {
  folder: Folder;
  canWrite?: boolean;
  onEdit?: (folder: Folder) => void;
  onDelete?: (folder: Folder) => void;
  onShare?: (folder: Folder) => void;
  onDownload?: (folder: Folder) => void;
}

export function FolderCard({
  folder,
  canWrite = true,
  onEdit,
  onDelete,
  onShare,
  onDownload,
}: FolderCardProps) {
  const navigate = useNavigate();

  const handleClick = () => {
    navigate(`/files/${folder.id}`);
  };

  return (
    <div
      onClick={handleClick}
      className="group bg-surface border border-border rounded-lg p-4 hover:shadow-md hover:border-brand-primary transition-all cursor-pointer"
    >
      <div className="flex items-start justify-between">
        <div className="flex items-center gap-3">
          <FolderIcon className="w-7 h-7 text-amber-400 shrink-0" fill="currentColor" strokeWidth={1.5} aria-hidden="true" />
          <div>
            <h3 className="font-medium text-foreground group-hover:text-brand-primary transition-colors">
              {folder.name}
            </h3>
            <p className="text-xs text-foreground-muted">{formatRelativeTime(folder.createdAt)}</p>
            {folder.isShared && (
              <div className="mt-0.5">
                <SharedBadge />
              </div>
            )}
          </div>
        </div>

        <ResourceActionMenu
          canWrite={canWrite}
          onDownload={onDownload ? () => onDownload(folder) : undefined}
          onShare={onShare ? () => onShare(folder) : undefined}
          onEdit={onEdit ? () => onEdit(folder) : undefined}
          onDelete={onDelete ? () => onDelete(folder) : undefined}
        />
      </div>

      {folder.description && (
        <p className="mt-2 text-sm text-foreground-muted truncate">{folder.description}</p>
      )}
    </div>
  );
}
